import { Suspense, lazy } from 'react'
import { Route, Routes } from 'react-router-dom'
import { Main as MainLayout, Minimal as MinimalLayout } from 'layouts'
import { PrivateRoute, PublicRoute } from 'components'
import { PageURLs } from 'config'

// Re-export so existing imports from 'Routes' keep working
export { PageURLs }

// ── Public pages ────────────────────────────────────────────────────────────
const HomeView = lazy(() => import('views/Home'))
const CategoriesView = lazy(() => import('views/Categories'))
const CategoryDetailView = lazy(() => import('views/CategoryDetail'))
const BrandsView = lazy(() => import('views/Brands'))
const BrandDetailView = lazy(() => import('views/BrandDetail'))
const ProductListingView = lazy(() => import('views/ProductListing'))
const ProductDetailsView = lazy(() => import('views/ProductDetails'))
const SearchResultsView = lazy(() => import('views/SearchResults'))
const CartView = lazy(() => import('views/Cart'))
const FavouritesView = lazy(() => import('views/Favourites'))
const OffersView = lazy(() => import('views/Offers'))
const CmsPageView = lazy(() => import('views/CmsPage'))
const NotFoundView = lazy(() => import('views/NotFound'))

// ── Auth-gated pages ─────────────────────────────────────────────────────────
const CheckoutView = lazy(() => import('views/Checkout'))
const OrderSuccessView = lazy(() => import('views/OrderSuccess'))
const OrderHistoryView = lazy(() => import('views/OrderHistory'))
const OrderDetailView = lazy(() => import('views/OrderDetail'))
const PayNowView = lazy(() => import('views/PayNow'))
const ProfileView = lazy(() => import('views/Profile'))
const ProfileAddressesView = lazy(() => import('views/ProfileAddresses'))
const ProfileSecurityView = lazy(() => import('views/ProfileSecurity'))
const SupportView = lazy(() => import('views/Support'))
const SupportNewView = lazy(() => import('views/SupportNew'))
const SupportTicketView = lazy(() => import('views/SupportTicket'))
const SettingsView = lazy(() => import('views/Settings'))

// ── Auth pages (public-only) ─────────────────────────────────────────────────
const LoginView = lazy(() => import('views/Login'))
const RegisterView = lazy(() => import('views/Register'))
const ForgotPasswordView = lazy(() => import('views/ForgotPassword'))

const RoutesComponent = () => {
  return (
    <Suspense fallback={null}>
      <Routes>
        {/* ── Main layout (nav + footer) ───────────────────────────────── */}
        <Route path="/" element={<MainLayout />}>
          {/* Public browsing */}
          <Route index element={<HomeView />} />
          <Route path="/categories" element={<CategoriesView />} />
          <Route path="/categories/:slug" element={<CategoryDetailView />} />
          <Route path="/brands" element={<BrandsView />} />
          <Route path="/brands/:slug" element={<BrandDetailView />} />
          <Route path="/products" element={<ProductListingView />} />
          <Route path="/products/:productId" element={<ProductDetailsView />} />
          <Route path="/products/search" element={<SearchResultsView />} />
          <Route path="/offers" element={<OffersView />} />
          <Route path="/pages/:slug" element={<CmsPageView />} />
          <Route path="/cart" element={<CartView />} />
          <Route path="/favourites" element={<FavouritesView />} />

          {/* Auth-gated */}
          <Route path="/checkout" element={<PrivateRoute><CheckoutView /></PrivateRoute>} />
          <Route path="/order-success/:orderId" element={<PrivateRoute><OrderSuccessView /></PrivateRoute>} />
          <Route path="/orders" element={<PrivateRoute><OrderHistoryView /></PrivateRoute>} />
          <Route path="/orders/:orderId" element={<PrivateRoute><OrderDetailView /></PrivateRoute>} />
          <Route path="/pay-now/:orderId" element={<PrivateRoute><PayNowView /></PrivateRoute>} />
          <Route path="/profile" element={<PrivateRoute><ProfileView /></PrivateRoute>} />
          <Route path="/profile/addresses" element={<PrivateRoute><ProfileAddressesView /></PrivateRoute>} />
          <Route path="/profile/security" element={<PrivateRoute><ProfileSecurityView /></PrivateRoute>} />
          <Route path="/support" element={<PrivateRoute><SupportView /></PrivateRoute>} />
          <Route path="/support/new" element={<PrivateRoute><SupportNewView /></PrivateRoute>} />
          <Route path="/support/:ticketId" element={<PrivateRoute><SupportTicketView /></PrivateRoute>} />
          <Route path="/settings" element={<PrivateRoute><SettingsView /></PrivateRoute>} />
        </Route>

        {/* ── Minimal layout (auth pages) ──────────────────────────────── */}
        <Route path="/" element={<MinimalLayout />}>
          <Route path="/login" element={<PublicRoute><LoginView /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><RegisterView /></PublicRoute>} />
          <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordView /></PublicRoute>} />
        </Route>

        {/* ── Fallback ─────────────────────────────────────────────────── */}
        <Route path="*" element={<NotFoundView />} />
      </Routes>
    </Suspense>
  )
}

export default RoutesComponent
